import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container, Button, Modal, Badge,
  Spinner, Alert, OverlayTrigger, Tooltip, Form
} from 'react-bootstrap';
import { CircularProgress, Box } from '@mui/material';
import DataTable from '../../../components/DataTable/DataTable';
import SupplierQualificationForm from './SupplierQualificationForm';
import DatosProveedorModal from './DatosProveedorModal';
import ExportarDatos from './ExportarDatos';
import ConfirmModal from '../../../components/ConfirmModal/ConfirmModal';

const ResultadosPage = () => {
  const [empresaSeleccionada, setEmpresaSeleccionada] = useState(null);
  const [mostrarModalCalificar, setMostrarModalCalificar] = useState(false);
  const [formularios, setFormularios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userInfo, setUserInfo] = useState(null);
  const [mensajeEvaluacion, setMensajeEvaluacion] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [calificaciones, setCalificaciones] = useState([]);
  const [creandoCalificar, setCreandoCalificar] = useState({});
  const [enviandoCorreo, setEnviandoCorreo] = useState(new Set());
  const [showDatosProveedor, setShowDatosProveedor] = useState(false);
  const [proveedorSeleccionado, setProveedorSeleccionado] = useState(null);
  const [showReactivarModal, setShowReactivarModal] = useState(false);
  const [reactivarId, setReactivarId] = useState(null);
  const [reactivarLoading, setReactivarLoading] = useState(false);
  const [fechasReactivacion, setFechasReactivacion] = useState({ fecha_inicio: '', fecha_fin: '' });
  const [showFinalizarConfirm, setShowFinalizarConfirm] = useState(false);
  const [finalizarId, setFinalizarId] = useState(null);
  const [finalizarLoading, setFinalizarLoading] = useState(false);
  const isMounted = useRef(true);

  const formatearFechaDB = (fechaString, incluirHora = false) => {
    if (!fechaString) return '—';
    if (incluirHora) {
      const [fecha, hora] = fechaString.replace('T', ' ').split(' ');
      const [year, month, day] = fecha.split('-');
      return `${day}/${month}/${year} ${hora.substring(0, 5)}`;
    }
    const [year, month, day] = fechaString.split('T')[0].split('-');
    return `${day}/${month}/${year}`;
  };

  const calcularPorcentajeExperiencia = useCallback((aniosExperiencia) => {
    if (aniosExperiencia <= 1) return 50;
    if (aniosExperiencia <= 2) return 75;
    if (aniosExperiencia <= 999) return 100;
    return 0;
  }, []);

  const calcularAniosExperiencia = useCallback((fechaInicio, fechaFin) => {
    if (!fechaInicio) return 0;
    const inicio = new Date(fechaInicio);
    const fin = fechaFin ? new Date(fechaFin) : new Date();
    const diffTiempo = Math.abs(fin - inicio);
    const diffDias = diffTiempo / (1000 * 60 * 60 * 24);
    return diffDias / 360;
  }, []);

  const estaCalificado = useCallback((formulario) => Number(formulario.calificado) === 1, []);
  const puedeCalificar = useCallback((formulario) => formulario.estado === 'finalizado', []);

  const cargarCalificaciones = useCallback(async () => {
    try {
      const res = await fetch('https://apiprov.conduto.com.ec/api/calificaciones-completas/list', {
        credentials: 'include'
      });
      if (res.ok && isMounted.current) {
        const data = await res.json();
        setCalificaciones(data);
      }
    } catch (err) {
      if (isMounted.current) {
        console.error('Error al cargar calificaciones:', err);
      }
    }
  }, []);

  const cargarFormularios = useCallback(async () => {
    try {
      let data = [];
      const resFormularios = await fetch('https://apiprov.conduto.com.ec/api/formularios-con-usuarios', {
        credentials: 'include'
      });

      if (!resFormularios.ok) {
        if (resFormularios.status === 403) {
          const resNormal = await fetch('https://apiprov.conduto.com.ec/api/formularios', {
            credentials: 'include'
          });
          if (resNormal.ok) data = await resNormal.json();
          else throw new Error('No autorizado para ver formularios');
        } else throw new Error('Error al cargar formularios');
      } else {
        data = await resFormularios.json();
      }

      if (isMounted.current) {
        data.sort((a, b) => b.id_formulario - a.id_formulario);
        const formulariosProcesados = data.map(formulario => ({
          ...formulario,
          calificado: Number(formulario.calificado) || 0,
          notificado: formulario.notificado || false,
          fecha_notificacion: formulario.fecha_notificacion || null,
          tipo_persona: formulario.usuario?.tipo_persona || formulario.tipo_persona,
          estado_visual: (formulario.estado === 'activo' && formulario.fecha_reactivacion)
            ? 'reactivado'
            : formulario.estado
        }));
        setFormularios(formulariosProcesados);
      }
    } catch (err) {
      if (isMounted.current) {
        console.error('Error al cargar formularios:', err);
        throw err;
      }
    }
  }, []);

  const cargarUserInfo = useCallback(async () => {
    try {
      const resUser = await fetch('https://apiprov.conduto.com.ec/api/protected-cookie', {
        credentials: 'include'
      });
      if (resUser.ok && isMounted.current) {
        const userData = await resUser.json();
        setUserInfo(userData.usuario);
      }
    } catch (err) {
      if (isMounted.current) {
        console.error('Error cargando info de usuario:', err);
      }
    }
  }, []);

  const cargarDatosCompletos = useCallback(async () => {
    try {
      setLoading(true);
      await Promise.all([
        cargarUserInfo(),
        cargarFormularios(),
        cargarCalificaciones()
      ]);
    } catch (err) {
      if (isMounted.current) {
        setError(err.message);
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, [cargarUserInfo, cargarFormularios, cargarCalificaciones]);

  useEffect(() => {
    isMounted.current = true;
    cargarDatosCompletos();
    const interval = setInterval(() => {
      cargarFormularios().catch(() => { });
      cargarCalificaciones().catch(() => { });
    }, 30000);
    return () => {
      isMounted.current = false;
      clearInterval(interval);
    };
  }, [cargarDatosCompletos, cargarFormularios, cargarCalificaciones]);

  const handleCerrarModalCalificar = useCallback(() => {
    setMostrarModalCalificar(false);
    setEmpresaSeleccionada(null);
    setMensajeEvaluacion('');
  }, []);

  const handleActualizarManual = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      await Promise.all([
        cargarFormularios(),
        cargarCalificaciones()
      ]);
      setMensajeEvaluacion('Tabla actualizada correctamente');
      setShowToast(true);
    } catch (err) {
      setError(err.message || 'Error al actualizar');
      setMensajeEvaluacion('Error al actualizar la tabla');
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  }, [cargarFormularios, cargarCalificaciones]);

  const crearCalificacion = useCallback(async (formularioId, tipoPersona) => {
    setMensajeEvaluacion('Creando calificación...');
    let data = null;
    let r = null;

    for (let intentos = 0; intentos < 2; intentos++) {
      try {
        r = await fetch('https://apiprov.conduto.com.ec/api/calificaciones', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            id_formulario: formularioId,
            id_usuario: userInfo?.id_usuario
          })
        });
        data = await r.json().catch(() => null);
        if (data) break;
      } catch (e) { }
    }

    if (!r || !r.ok) {
      setMensajeEvaluacion(`Error: ${data?.detail || data?.error || 'Error desconocido del servidor'}`);
      setShowToast(true);
      return null;
    }

    setMensajeEvaluacion('Calificación generada');
    setShowToast(true);
    return data?.calificacion;
  }, [userInfo]);

  const handleCalificar = useCallback(async (formulario) => {
    setCreandoCalificar(prev => ({ ...prev, [formulario.id_formulario]: true }));
    try {
      const cal = await crearCalificacion(formulario.id_formulario, formulario.tipo_persona);
      if (cal) {
        setEmpresaSeleccionada({ ...formulario, calificacion: cal });
        setMostrarModalCalificar(true);
      }
    } catch (e) {
      setError('Error al iniciar la calificación');
      setShowToast(true);
    } finally {
      setCreandoCalificar(prev => ({ ...prev, [formulario.id_formulario]: false }));
    }
  }, [crearCalificacion]);

  const openReactivarModal = useCallback((formulario) => {
    setReactivarId(formulario.id_formulario);
    setFechasReactivacion({
      fecha_inicio: formulario.fecha_inicio || '',
      fecha_fin: formulario.fecha_fin || ''
    });
    setShowReactivarModal(true);
  }, []);

  const confirmReactivar = useCallback(async () => {
    if (!reactivarId) return;

    setReactivarLoading(true);
    try {
      const response = await fetch(`https://apiprov.conduto.com.ec/api/formularios/${reactivarId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          estado: 'activo',
          fecha_inicio: fechasReactivacion.fecha_inicio,
          fecha_fin: fechasReactivacion.fecha_fin
        })
      });

      if (response.ok) {
        const data = await response.json();
        setFormularios(prev => prev.map(f =>
          f.id_formulario === reactivarId ? {
            ...f,
            estado: 'activo',
            fecha_reactivacion: data.formulario?.fecha_reactivacion || new Date().toISOString(),
            fecha_inicio: fechasReactivacion.fecha_inicio || f.fecha_inicio,
            fecha_fin: fechasReactivacion.fecha_fin || f.fecha_fin
          } : f
        ));
        setMensajeEvaluacion('Formulario reactivado exitosamente');
        setShowToast(true);
        setShowReactivarModal(false);
        setReactivarId(null);
        setFechasReactivacion({ fecha_inicio: '', fecha_fin: '' });
        await cargarFormularios();
        await cargarCalificaciones();
      } else {
        const errorData = await response.json().catch(() => ({}));
        setMensajeEvaluacion(`Error: ${errorData.detail || 'Error al reactivar'}`);
        setShowToast(true);
      }
    } catch (error) {
      setMensajeEvaluacion(`Error: ${error.message}`);
      setShowToast(true);
    } finally {
      setReactivarLoading(false);
    }
  }, [reactivarId, fechasReactivacion, cargarFormularios, cargarCalificaciones]);

  const openFinalizarModal = useCallback((formularioId) => {
    setFinalizarId(formularioId);
    setShowFinalizarConfirm(true);
  }, []);

  const confirmFinalizar = useCallback(async () => {
    if (!finalizarId) return;

    setFinalizarLoading(true);
    try {
      const response = await fetch(`https://apiprov.conduto.com.ec/api/formularios/${finalizarId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ estado: 'finalizado', visible: 0 })
      });

      if (response.ok) {
        setFormularios(prev => prev.map(f =>
          f.id_formulario === finalizarId ? {
            ...f,
            estado: 'finalizado',
            visible: 0
          } : f
        ));
        setMensajeEvaluacion('Formulario finalizado exitosamente');
        setShowToast(true);
        setShowFinalizarConfirm(false);
        setFinalizarId(null);
        await cargarFormularios();
        await cargarCalificaciones();
      } else {
        const errorData = await response.json().catch(() => ({}));
        setMensajeEvaluacion(`Error: ${errorData.detail || 'Error al finalizar'}`);
        setShowToast(true);
      }
    } catch (error) {
      setMensajeEvaluacion(`Error: ${error.message}`);
      setShowToast(true);
    } finally {
      setFinalizarLoading(false);
    }
  }, [finalizarId, cargarFormularios, cargarCalificaciones]);

  const enviarResultadoPorCorreo = useCallback(async (idFormulario) => {
    if (!idFormulario) return;
    setEnviandoCorreo(prev => new Set(prev).add(idFormulario));
    setMensajeEvaluacion('');
    try {
      const res = await fetch(`https://apiprov.conduto.com.ec/api/formularios/${idFormulario}/notificar`, {
        method: 'POST',
        credentials: 'include'
      });

      if (res.ok) {
        const data = await res.json();
        setFormularios(prev => prev.map(f =>
          f.id_formulario === idFormulario ? {
            ...f,
            notificado: true,
            fecha_notificacion: data.fecha_notificacion
          } : f
        ));
        setMensajeEvaluacion('Notificación enviada con éxito.');
      } else {
        setMensajeEvaluacion('Error al enviar notificación.');
      }
    } catch (e) {
      setMensajeEvaluacion('Error al enviar notificación.');
    } finally {
      setEnviandoCorreo(prev => {
        const n = new Set(prev);
        n.delete(idFormulario);
        return n;
      });
      setShowToast(true);
    }
  }, []);

  const handleVerDatosProveedor = useCallback((formulario) => {
    setProveedorSeleccionado(formulario);
    setShowDatosProveedor(true);
  }, []);

  const getEstadoBadge = useCallback((formulario) => {
    const isReactivado = formulario.estado === 'activo' && formulario.fecha_reactivacion;

    let estadoVisual = formulario.estado;
    let variant = '';
    let tooltipText = '';

    if (isReactivado) {
      estadoVisual = 'reactivado';
      variant = 'warning';
      tooltipText = `Reactivado: ${formatearFechaDB(formulario.fecha_reactivacion, true)}`;
    } else {
      switch (formulario.estado) {
        case 'activo':
          variant = 'success';
          break;
        case 'pendiente':
          variant = 'warning';
          break;
        case 'finalizado':
          variant = 'info';
          break;
        case 'cancelado':
          variant = 'secondary';
          break;
        default:
          variant = 'secondary';
      }
    }

    const badge = (
      <Badge bg={variant} className="rounded-pill">
        {estadoVisual}
      </Badge>
    );

    if (tooltipText) {
      return (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip id={`tooltip-estado-${formulario.id_formulario}`}>{tooltipText}</Tooltip>}
        >
          {badge}
        </OverlayTrigger>
      );
    }

    return badge;
  }, []);

  const getTipoBadge = useCallback((t) => (
    <Badge bg={t === 'juridica' ? 'primary' : 'secondary'} className="rounded-pill">
      {t === 'juridica' ? 'Jurídica' : 'Natural'}
    </Badge>
  ), []);

  const getCalificacionBadge = useCallback((totalObtenido) => {
    if (totalObtenido === null || totalObtenido === undefined || totalObtenido === 0) {
      return <Badge bg="secondary">Sin calificar</Badge>;
    }
    const calificacionFinal = parseFloat(totalObtenido);
    let bgColor = 'secondary';
    let texto = '';
    if (calificacionFinal >= 100) {
      bgColor = 'success';
      texto = 'Excelente';
    } else if (calificacionFinal >= 75) {
      bgColor = 'info';
      texto = 'Muy Bueno';
    } else if (calificacionFinal >= 50) {
      bgColor = 'primary';
      texto = 'Bueno';
    } else {
      bgColor = 'danger';
      texto = 'Insuficiente';
    }
    return (
      <Badge bg={bgColor}>
        {calificacionFinal.toFixed(1)} - {texto}
      </Badge>
    );
  }, []);

  const obtenerCalificacionProveedor = useCallback((idFormulario) => {
    const calificacion = calificaciones.find(c => c.id_formulario === idFormulario);
    if (!calificacion || !calificacion.calificacion) return null;
    return {
      totalObtenido: calificacion.calificacion.total_obtenido || 0,
      resultado: calificacion.calificacion.resultado,
      fechaCalificacion: calificacion.calificacion.fecha_calificacion,
      porcentaje: calificacion.calificacion.porcentaje || 0
    };
  }, [calificaciones]);

  const obtenerExperienciaProveedor = useCallback((formulario) => {
    const aniosExperiencia = calcularAniosExperiencia(
      formulario.fecha_inicio,
      formulario.fecha_actualizacion
    );
    const porcentaje = calcularPorcentajeExperiencia(aniosExperiencia);
    return {
      anios: aniosExperiencia,
      porcentaje: porcentaje
    };
  }, [calcularAniosExperiencia, calcularPorcentajeExperiencia]);

  const columns = [
    {
      key: 'id_formulario',
      label: 'ID',
      sortable: false
    },
    {
      key: 'titulo_formulario_general',
      label: 'Formulario',
      sortable: false,
      style: { maxWidth: 200, whiteSpace: 'normal', wordBreak: 'break-word' }
    },
    {
      key: 'proveedor',
      label: 'Proveedor',
      sortable: false,
      render: (f) => (
        <div style={{ maxWidth: 200, whiteSpace: 'normal', wordBreak: 'break-word' }}>
          <div className="fw-medium">{f.usuario?.nombre || '—'}</div>
          <small className="text-muted">
            {f.usuario?.tipo_persona || '—'}
          </small>
        </div>
      )
    },
    {
      key: 'fecha_inicio',
      label: 'Periodo',
      sortable: true,
      isDate: true,
      render: (f) => {
        const inicio = f.fecha_inicio ? formatearFechaDB(f.fecha_inicio) : '—';
        const fin = f.fecha_fin ? formatearFechaDB(f.fecha_fin) : '—';
        return <div>{inicio}<br />al {fin}</div>;
      }
    },
    {
      key: 'fecha_finalizacion',
      label: 'Fecha Finalización',
      sortable: true,
      isDate: true,
      render: (f) => {
        if (f.estado === 'finalizado' && f.fecha_finalizacion) {
          return formatearFechaDB(f.fecha_finalizacion, true);
        }
        return '—';
      }
    },
    {
      key: 'estado',
      label: 'Estado',
      sortable: false,
      render: (f) => getEstadoBadge(f)
    },
    {
      key: 'calificacion',
      label: 'Calificación',
      sortable: false,
      render: (f) => {
        const calificacion = obtenerCalificacionProveedor(f.id_formulario);
        return calificacion ? (
          <div>
            <Badge bg="info">{calificacion.totalObtenido.toFixed(2)} / 100</Badge>
          </div>
        ) : (
          <Badge bg="secondary">Sin calificar</Badge>
        );
      }
    },
    {
      key: 'fecha_calificacion',
      label: 'Fecha Calificación',
      sortable: true,
      isDate: true,
      sortValue: (f) => {
        const calificacion = obtenerCalificacionProveedor(f.id_formulario);
        return calificacion?.fechaCalificacion || null;
      },
      render: (f) => {
        const calificacion = obtenerCalificacionProveedor(f.id_formulario);
        return calificacion?.fechaCalificacion ?
          formatearFechaDB(calificacion.fechaCalificacion, true) : '—';
      }
    }
  ];

  const extraColumns = [
    {
      render: (formulario) => (
        <Button
          size="sm"
          variant={estaCalificado(formulario) ? "success" : puedeCalificar(formulario) ? "primary" : "outline-secondary"}
          onClick={() => handleCalificar(formulario)}
          disabled={!puedeCalificar(formulario)}
          style={{ minWidth: '90px' }}
        >
          {creandoCalificar[formulario.id_formulario] ? (
            <Spinner animation="border" size="sm" />
          ) : estaCalificado(formulario) ? (
            'Calificado'
          ) : (
            'Calificar'
          )}
        </Button>
      )
    }
  ];

  const filterConfig = [
    {
      key: 'tipo_persona',
      label: 'Todos los tipos',
      options: [
        { value: 'natural', label: 'Persona Natural' },
        { value: 'juridica', label: 'Persona Jurídica' }
      ]
    },
    {
      key: 'estado_visual',
      label: 'Todos los estados',
      options: [
        { value: 'activo', label: 'Activo' },
        { value: 'finalizado', label: 'Finalizado' },
        { value: 'reactivado', label: 'Reactivado' }
      ]
    },

  ];

  const rowActions = useCallback((formulario) => {
    const isActivo = formulario.estado === 'activo';
    const isFinalizado = formulario.estado === 'finalizado';
    const isPendiente = formulario.estado === 'pendiente';

    return (
      <div className="d-flex gap-2 justify-content-end">
        {(isFinalizado || isPendiente) && (
          <Button
            variant="outline-warning"
            size="sm"
            onClick={() => openReactivarModal(formulario)}
            title="Reactivar / Editar fechas"
          >
            <i className="bi bi-arrow-clockwise" />
          </Button>
        )}
        {isActivo && (
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => openFinalizarModal(formulario.id_formulario)}
            title="Finalizar formulario"
          >
            <i className="bi bi-check2-circle" />
          </Button>
        )}
        <Button
          variant="outline-info"
          size="sm"
          onClick={() => handleVerDatosProveedor(formulario)}
          title="Ver datos del proveedor"
        >
          <i className="bi bi-eye" />
        </Button>
        <OverlayTrigger
          placement="top"
          overlay={
            formulario.subido ? (
              <Tooltip id={`tooltip-subido-${formulario.id_formulario}`}>
                Ya está subido al sistema
              </Tooltip>
            ) : (
              <></>
            )
          }
        >
          <span>
            <Button
              size="sm"
              variant={
                formulario.subido
                  ? "success"
                  : formulario.notificado
                    ? "success"
                    : "outline-primary"
              }
              onClick={() => {
                if (!formulario.subido) {
                  enviarResultadoPorCorreo(formulario.id_formulario);
                }
              }}
              disabled={
                enviandoCorreo.has(formulario.id_formulario) ||
                formulario.estado !== "finalizado" ||
                !formulario.calificado
              }
              style={
                formulario.subido
                  ? { pointerEvents: "none" }
                  : {}
              }
            >
              {formulario.subido ? (
                <i className="bi bi-check-circle-fill" />
              ) : enviandoCorreo.has(formulario.id_formulario) ? (
                <Spinner animation="border" size="sm" />
              ) : formulario.notificado ? (
                <i className="bi bi-send-check" />
              ) : (
                <i className="bi bi-send" />
              )}
            </Button>
          </span>
        </OverlayTrigger>
      </div>
    );
  }, [enviandoCorreo, handleVerDatosProveedor, enviarResultadoPorCorreo, openReactivarModal, openFinalizarModal]);

  const mobileCardRenderer = useCallback((formulario) => {
    const calificacion = obtenerCalificacionProveedor(formulario.id_formulario);
    const experiencia = obtenerExperienciaProveedor(formulario);
    const isReactivado = formulario.estado === 'activo' && formulario.fecha_reactivacion;
    const isActivo = formulario.estado === 'activo';
    const isFinalizado = formulario.estado === 'finalizado';
    const isPendiente = formulario.estado === 'pendiente';

    return (
      <div>
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div className="fw-bold text-primary">#{formulario.id_formulario}</div>
          <div>
            {getTipoBadge(formulario.tipo_persona)}
            <span className="ms-1">{getEstadoBadge(formulario)}</span>
          </div>
        </div>
        <div className="mb-2">
          <div className="fw-semibold fs-6">{formulario.titulo_formulario_general}</div>
          <div className="text-muted small">@{formulario.usuario?.usuario || '—'}</div>
        </div>
        <div className="mb-2">
          <div className="fw-medium">{formulario.usuario?.nombre || '—'}</div>
          <div className="text-truncate small text-muted">{formulario.usuario?.correo || '—'}</div>
        </div>
        {isReactivado && (
          <div className="mt-2">
            <small className="text-muted d-block">
              <i className="bi bi-calendar-plus me-1"></i>
              Reactivado: {formatearFechaDB(formulario.fecha_reactivacion, true)}
            </small>
          </div>
        )}
        <div className="row g-2 mb-2">
          <div className="col-6">
            <small className="text-muted d-block">Periodo</small>
            <div className="fw-medium">
              {formulario.fecha_inicio ? formatearFechaDB(formulario.fecha_inicio) : '—'}
              <br />
              al {formulario.fecha_fin ? formatearFechaDB(formulario.fecha_fin) : '—'}
            </div>
          </div>
          <div className="col-6">
            <small className="text-muted d-block">Fecha Finalización</small>
            <div className="fw-medium">
              {formulario.estado === 'finalizado' && formulario.fecha_finalizacion ?
                formatearFechaDB(formulario.fecha_finalizacion, true) : '—'}
            </div>
          </div>
        </div>
        <div className="mb-3">
          <small className="text-muted d-block">Calificación</small>
          <div>
            {calificacion ?
              getCalificacionBadge(calificacion.totalObtenido) :
              <Badge bg="secondary">Sin calificar</Badge>
            }
          </div>
        </div>
        <div className="d-flex gap-2 mt-3 pt-2 border-top">
          {(isFinalizado || isPendiente) && (
            <Button
              variant="outline-warning"
              size="sm"
              onClick={() => openReactivarModal(formulario)}
              className="flex-fill"
            >
              <i className="bi bi-arrow-clockwise me-1"></i>
              Reactivar
            </Button>
          )}
          {isActivo && (
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => openFinalizarModal(formulario.id_formulario)}
              className="flex-fill"
            >
              <i className="bi bi-check2-circle me-1"></i>
              Finalizar
            </Button>
          )}
          <Button
            variant="outline-info"
            size="sm"
            onClick={() => handleVerDatosProveedor(formulario)}
            className="flex-fill"
          >
            <i className="bi bi-eye me-1"></i>
            Ver
          </Button>
          <Button
            size="sm"
            variant={estaCalificado(formulario) ? "success" : puedeCalificar(formulario) ? "primary" : "outline-secondary"}
            onClick={() => handleCalificar(formulario)}
            disabled={!puedeCalificar(formulario)}
            className="flex-fill"
          >
            {creandoCalificar[formulario.id_formulario] ? (
              <Spinner animation="border" size="sm" />
            ) : estaCalificado(formulario) ? (
              'Calificado'
            ) : (
              'Calificar'
            )}
          </Button>
        </div>
      </div>
    );
  }, [getTipoBadge, getEstadoBadge, getCalificacionBadge, obtenerCalificacionProveedor, obtenerExperienciaProveedor, creandoCalificar, estaCalificado, puedeCalificar, handleVerDatosProveedor, handleCalificar, openReactivarModal, openFinalizarModal]);

  if (loading && formularios.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container fluid className="px-4 py-4">
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
          {error.includes('No autorizado') && (
            <p>No tienes permisos para ver esta información. Contacta al administrador.</p>
          )}
        </Alert>
      </Container>
    );
  }

  return (
    <>
      <DataTable
        data={formularios}
        loading={loading}
        error={error}
        totalItems={formularios.length}
        title="Resultados de Proveedores / Subcontratistas"
        subtitle="Mostrando"
        columns={columns}
        extraColumns={[
          {
            label: 'Acciones',
            render: rowActions
          },
          ...extraColumns
        ]}
        filterConfig={filterConfig}
        mobileCardRenderer={mobileCardRenderer}
        headerActions={
          <div className="d-flex align-items-center gap-2">
            <Button
              variant="outline-primary"
              onClick={handleActualizarManual}
              className="d-flex align-items-center"
              disabled={loading}
            >
              {loading ? (
                <Spinner animation="border" size="sm" className="me-2" />
              ) : (
                <i className="bi bi-arrow-clockwise me-2"></i>
              )}
            </Button>
            <ExportarDatos formularios={formularios} calificaciones={calificaciones} />
          </div>
        }
        searchPlaceholder="Buscar por ID, título, proveedor o email..."
        snackbar={{ open: showToast, text: mensajeEvaluacion, severity: mensajeEvaluacion.includes('Error') ? 'error' : 'success' }}
        onCloseSnackbar={() => setShowToast(false)}
        customFilterFunction={(item, filters) => {
          let matches = true;

          if (filters.tipo_persona && item.tipo_persona !== filters.tipo_persona) {
            matches = false;
          }

          if (filters.estado_visual) {
            const estadoVisual = (item.estado === 'activo' && item.fecha_reactivacion) ? 'reactivado' : item.estado;
            if (estadoVisual !== filters.estado_visual) {
              matches = false;
            }
          }

          if (filters.calificado_filter) {
            const tieneCalificacion = obtenerCalificacionProveedor(item.id_formulario) !== null;
            if (filters.calificado_filter === 'calificado' && !tieneCalificacion) {
              matches = false;
            }
            if (filters.calificado_filter === 'no_calificado' && tieneCalificacion) {
              matches = false;
            }
          }

          return matches;
        }}
      />

      <Modal show={mostrarModalCalificar} onHide={handleCerrarModalCalificar} size="xl" scrollable>
        <Modal.Header className="bg-light">
          <div className="w-100">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0" style={{ color: '#00406bff' }}>
                <i className="bi bi-clipboard-data me-2" />
                SELECCIÓN Y CALIFICACIÓN DE PROVEEDORES
              </h5>
              <small className="text-muted">
                <strong>1-FO-PR-CCC-SEDE-001-23</strong>
              </small>
            </div>
            {empresaSeleccionada && (
              <div className="row mt-3">
                <div className="col-6 text-start">
                  <small className="text-muted d-block">
                    <strong>Formulario:</strong> {empresaSeleccionada.titulo_formulario_general}
                  </small>
                  <small className="text-muted d-block">
                    <strong>Persona:</strong> {empresaSeleccionada.usuario?.tipo_persona || 'N/A'}
                  </small>
                </div>
                <div className="col-6 text-end">
                  <small className="text-muted d-block">
                    <strong>Reporte No.:</strong> {empresaSeleccionada.id_formulario || 'N/A'}
                  </small>
                  <small className="text-muted d-block">
                    <strong>Fecha de Calificación:</strong>
                    {' '}
                    {empresaSeleccionada.calificacion?.fecha_calificacion
                      ? formatearFechaDB(empresaSeleccionada.calificacion.fecha_calificacion, true)
                      : 'N/A'}
                  </small>
                </div>
              </div>
            )}
          </div>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
          {creandoCalificar[empresaSeleccionada?.id_formulario] ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <p className="mt-2">{mensajeEvaluacion}</p>
            </div>
          ) : empresaSeleccionada && (
            <SupplierQualificationForm
              proveedorId={empresaSeleccionada.id_usuario}
              proveedorNombre={empresaSeleccionada.usuario?.nombre}
              proveedorEmail={empresaSeleccionada.usuario?.correo}
              formularioId={empresaSeleccionada.id_formulario}
              tipoPersona={empresaSeleccionada.usuario?.tipo_persona}
              formularioData={empresaSeleccionada}
              calificacionData={empresaSeleccionada.calificacion}
              onCalificacionCompletada={cargarDatosCompletos}
              calcularPorcentajeExperiencia={calcularPorcentajeExperiencia}
              calcularAniosExperiencia={calcularAniosExperiencia}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCerrarModalCalificar}>
            <i className="bi bi-x-circle me-1" />Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      <DatosProveedorModal
        show={showDatosProveedor}
        handleClose={() => {
          setShowDatosProveedor(false);
          setProveedorSeleccionado(null);
        }}
        formulario={proveedorSeleccionado}
      />

      <Modal show={showReactivarModal} onHide={() => setShowReactivarModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-arrow-repeat me-2"></i>
            Reactivar Formulario
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Puedes modificar las fechas de disponibilidad del formulario:</p>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Fecha de Inicio</Form.Label>
              <Form.Control
                type="date"
                value={fechasReactivacion.fecha_inicio ? fechasReactivacion.fecha_inicio.split('T')[0] : ''}
                onChange={(e) => setFechasReactivacion(prev => ({ ...prev, fecha_inicio: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Fecha de Fin</Form.Label>
              <Form.Control
                type="date"
                value={fechasReactivacion.fecha_fin ? fechasReactivacion.fecha_fin.split('T')[0] : ''}
                onChange={(e) => setFechasReactivacion(prev => ({ ...prev, fecha_fin: e.target.value }))}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReactivarModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={confirmReactivar} disabled={reactivarLoading}>
            {reactivarLoading ? <Spinner animation="border" size="sm" className="me-2" /> : null}
            Reactivar
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmModal
        show={showFinalizarConfirm}
        onHide={() => {
          setShowFinalizarConfirm(false);
          setFinalizarId(null);
        }}
        onConfirm={confirmFinalizar}
        title="¿Estás seguro de finalizar este formulario?"
        message="Al finalizar el formulario, el proveedor perderá acceso y no podrá continuar con el proceso."
        confirmText="Finalizar"
        cancelText="Cancelar"
        confirmVariant="danger"
        loading={finalizarLoading}
        icon={<i className="bi bi-exclamation-triangle text-danger" style={{ fontSize: '3rem' }}></i>}
      />
    </>
  );
};

export default ResultadosPage;